import { LitElement, html, nothing } from "lit";
import type { PropertyValues } from "lit";

import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { styles } from "./social-media-item.css";
import { DnDItemController } from "../item-card/dnd-item-controller";
import { createRef, ref } from "lit/directives/ref.js";

import "@phosphor-icons/webcomponents/PhDotsSixVertical";
import { ifDefined } from "lit/directives/if-defined.js";

export interface SocialMediaItemData {
  icon: string;
  platformName: string;
  link?: string;
  showCTA?: boolean;
  editable?: boolean;
  mode?: "display" | "edit" | "add-button";
  autoFocus?: boolean;
  addText?: string;
  addAriaLabel?: string;
  placeholderText?: string;
  showDelete?: boolean;
  deleteAriaLabel?: string;
}

@customElement("social-media-item")
export class SocialMediaItem extends LitElement {
  static override styles = styles;

  @property({ type: Number })
  index?: number;

  @property({ type: String })
  icon = "";

  @property({ type: String })
  platformName = "";

  @property({ type: String })
  platform = ""; // Platform key (e.g., "instagram")

  @property({ type: String })
  link: string = "";

  @property({ type: Boolean })
  showCTA = false;

  @property({ type: Boolean })
  editable = false;

  @property({ type: String })
  mode: "display" | "edit" | "add-button" = "display";

  @property({ type: Boolean })
  autoFocus = false;

  @property({ type: String })
  addText = "+ Add";

  @property({ type: String })
  addAriaLabel = "";

  @property({ type: String })
  placeholderText = "";

  @property({ type: Boolean })
  isAnimating = false;

  @property({ type: Boolean })
  showDelete = false;

  @property({ type: String })
  deleteAriaLabel = "";

  @property({ type: Boolean })
  hasError = false;

  @property({ type: String })
  errorMessage = "";

  @property({ type: Boolean })
  showValidation = false;

  @property({ type: Boolean })
  flourish = false;

  // Create ref for drag handle
  private dragHandleRef = createRef<HTMLElement>();

  // Set up the DnD item controller
  private dndController = new DnDItemController(
    this,
    { id: this.link },
    {
      dragHandle: this.dragHandleRef,
    },
  );

  // Expose dragging state
  get isDragging() {
    return this.dndController.isCurrentlyDragging;
  }

  protected override render() {
    const isAddButton = this.mode === "add-button";

    const classes = {
      "social-media-item": true,
      "add-button-item": isAddButton,
      entering: this.isAnimating,
      "draggable-item": this.mode === "edit",
      "flourish-active": this.flourish,
      dragging: this.isDragging,
    };

    const ariaLabel = isAddButton ? this.addAriaLabel || `Add ${this.platformName} link` : nothing;

    return html`
      <section
        data-item-id=${this.link}
        class=${classMap(classes)}
        role=${ifDefined(isAddButton ? "button" : undefined)}
        tabindex=${ifDefined(isAddButton ? "0" : undefined)}
        aria-label=${ariaLabel}
        @click=${this.handleAddClick}
        @keydown=${this.handleKeyDown}
      >
        ${this.mode === "edit"
          ? html`<div class="item-card-dnd-handle-wrapper">
              <div class="item-card-dnd-handle-hitarea" ${ref(this.dragHandleRef)}></div>
              <div class="item-card-dnd-handle">
                <ph-dots-six-vertical size="20"></ph-dots-six-vertical>
              </div>
            </div>`
          : nothing}
        ${this.icon
          ? html`<img
              src=${this.icon}
              alt="${this.platformName} icon"
              class="social-icon"
              @click=${this.handlePlateformClick}
            />`
          : html`<ph-question size="2rem"></ph-question>`}

        <div class="content-section">
          <p class="platform-name">${this.platformName}</p>
          ${this.renderContent()}
        </div>
        ${this.renderCTASection()}
      </section>
    `;
  }

  private renderContent() {
    switch (this.mode) {
      case "edit":
        return this.renderEditableInput();
      case "add-button":
        return nothing; // No content for add button
      case "display":
      default:
        return this.editable
          ? this.renderEditableInput()
          : this.link
            ? html`<p class="social-link">${this.link}</p>`
            : nothing;
    }
  }

  private renderEditableInput() {
    const placeholder =
      this.placeholderText || this.platformName === "Email"
        ? "Enter your email"
        : `Enter your ${this.platformName} link`;

    return html`
      <input
        type="text"
        class=${classMap({
          "editable-link": true,
          error: this.hasError && this.showValidation,
        })}
        .value=${this.link || ""}
        placeholder=${placeholder}
        @input=${this.handleLinkInput}
        @focus=${this.handleInputFocus}
        @blur=${this.handleLinkBlur}
      />
      ${this.hasError && this.showValidation ? html`<div class="error-message">${this.errorMessage}</div>` : nothing}
    `;
  }

  private renderCTASection() {
    if (this.mode === "add-button") {
      return html`
        <div class="cta-section">
          <span class="add-text">${this.addText}</span>
        </div>
      `;
    }

    if (this.showCTA || this.showDelete) {
      return html`
        <div class="cta-section">
          ${this.showCTA ? html`<slot name="cta"></slot>` : nothing}
          ${this.showDelete ? this.renderDeleteButton() : nothing}
        </div>
      `;
    }

    return nothing;
  }

  private renderDeleteButton() {
    return html`
      <button class="delete-button" @click=${this.handleDeleteClick} aria-label=${this.deleteAriaLabel}>
        <ph-trash-simple size="16"></ph-trash-simple>
      </button>
    `;
  }

  private handleLinkInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.dispatchEvent(
      new CustomEvent("link-input", {
        detail: { value: input.value, originalUrl: this.link },
        bubbles: true,
      }),
    );
  }

  private handleLinkBlur(event: Event) {
    const input = event.target as HTMLInputElement;
    this.dispatchEvent(
      new CustomEvent("link-blur", {
        detail: { value: input.value, originalUrl: this.link },
        bubbles: true,
      }),
    );
  }

  private handlePlateformClick() {
    this.dispatchEvent(
      new CustomEvent("edit-platform-clicked", {
        detail: { platform: this.platformName, index: this.index },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleAddClick() {
    if (this.mode !== "add-button") return;

    this.dispatchEvent(
      new CustomEvent("add-link-clicked", {
        detail: { platform: this.platform }, // Use platform key instead of platformName
        bubbles: true,
      }),
    );
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (this.mode !== "add-button") return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.handleAddClick();
    }
  }

  private handleInputFocus() {
    this.dispatchEvent(
      new CustomEvent("focused", {
        bubbles: true,
      }),
    );
  }

  private handleDeleteClick(event: Event) {
    event.stopPropagation(); // Prevent triggering parent click handlers
    this.dispatchEvent(
      new CustomEvent("delete-link", {
        detail: { originalUrl: this.link },
        bubbles: true,
      }),
    );
  }

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (this.autoFocus && (this.mode === "edit" || this.editable)) {
      const input = this.shadowRoot?.querySelector(".editable-link") as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }

    if (changedProperties.has("link") && this.mode === "edit") {
      this.dndController.updateItemData({ id: this.link });
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "social-media-item": SocialMediaItem;
  }
}
